/**
 * Папки чатов (TG-style folders): tab-bar над списком топиков с переключением
 * фильтра + кнопка "Управление папками" (создать/переименовать/удалить).
 */
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchChatFolders, createChatFolder, editChatFolder, deleteChatFolder,
  fetchChatTopics,
  type ChatFolder,
} from "@/api/dashboard";
import { Icon } from "@/components/prototype/Icon";
import { parseFolderIcon } from "@/data/folderIcons";
import { NewFolderModal } from "@/components/NewFolderModal";

export function FoldersBar({
  activeFolderId, onChange,
}: {
  activeFolderId: number | null;  // null = «Все»
  onChange: (folderId: number | null) => void;
}) {
  const qc = useQueryClient();
  const [manageOpen, setManageOpen] = useState(false);
  const [folderCtx, setFolderCtx] = useState<{ folder: ChatFolder; x: number; y: number } | null>(null);
  const [editFolder, setEditFolder] = useState<ChatFolder | null>(null);

  // Close ctx on outside click / Esc. iOS PWA: после long-press iOS фаерит
  // synthetic click ~300ms спустя touchend, который мог бы закрыть меню до
  // его появления на экране. Отложим вешание click-listener на 400ms.
  useEffect(() => {
    if (!folderCtx) return;
    const close = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && t.closest(".folder-ctx-menu")) return;
      setFolderCtx(null);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setFolderCtx(null); };
    const timer = window.setTimeout(() => {
      document.addEventListener("click", close);
    }, 400);
    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("click", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [folderCtx]);


  const removeMut = useMutation({
    mutationFn: (id: number) => deleteChatFolder(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat/folders"] }),
  });

  const foldersQ = useQuery({
    queryKey: ["chat/folders"],
    queryFn: fetchChatFolders,
    staleTime: 60_000,
  });
  const topicsQ = useQuery({
    queryKey: ["chat/topics"],
    queryFn: fetchChatTopics,
    staleTime: 60_000,
  });
  const folders = foldersQ.data ?? [];
  const topics = topicsQ.data ?? [];

  // Bypass-route: global long-press dispatches CustomEvent — открываем меню напрямую
  useEffect(() => {
    const onLongPress = (e: Event) => {
      const ce = e as CustomEvent<{ folderId: number; x: number; y: number }>;
      const f = folders.find(x => x.id === ce.detail.folderId);
      if (!f) return;
      // Close other ctx menus before opening this one
      document.dispatchEvent(new CustomEvent("kb-close-ctx", { detail: { except: "folder" } }));
      setFolderCtx({ folder: f, x: ce.detail.x, y: ce.detail.y });
    };
    const onCloseCtx = (e: Event) => {
      const ce = e as CustomEvent<{ except?: string }>;
      if (ce.detail?.except !== "folder") setFolderCtx(null);
    };
    document.addEventListener("kb-folder-ctx", onLongPress as EventListener);
    document.addEventListener("kb-close-ctx", onCloseCtx as EventListener);
    return () => {
      document.removeEventListener("kb-folder-ctx", onLongPress as EventListener);
      document.removeEventListener("kb-close-ctx", onCloseCtx as EventListener);
    };
  }, [folders]);


  return (
    <>
      <div style={{
        display: "flex", alignItems: "stretch", gap: 0,
        padding: "0 8px",
        overflowX: "auto",
        overflowY: "hidden",
        scrollbarWidth: "thin",
        borderBottom: "1px solid var(--line)",
        flexShrink: 0,
        minHeight: 36,
        background: "var(--paper-2)",
      }}>
        <FolderTab
          active={activeFolderId === null}
          label="Все"
          onClick={() => onChange(null)}
        />
        {folders.map(f => {
          const parsed = parseFolderIcon(f.icon);
          return (
            <FolderTab
              key={f.id}
              folderId={f.id}
              folderName={f.name}
              active={activeFolderId === f.id}
              svgPath={parsed.kind === "svg" ? parsed.def.path : undefined}
              emoji={parsed.kind === "emoji" ? parsed.emoji : undefined}
              label={f.name}
              onClick={() => onChange(f.id)}
              onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                document.dispatchEvent(new CustomEvent("kb-close-ctx", { detail: { except: "folder" } }));
                setFolderCtx({ folder: f, x: e.clientX, y: e.clientY });
              }}
            />
          );
        })}
        <button
          type="button"
          onClick={() => setManageOpen(true)}
          title="Создать / редактировать папки"
          style={{
            display: "flex", alignItems: "center", gap: 4,
            background: "transparent",
            border: "1px dashed var(--line)",
            borderRadius: 12,
            color: "var(--mute)", cursor: "pointer",
            padding: "3px 10px",
            marginLeft: 6, alignSelf: "center",
            fontSize: 11, fontWeight: 500,
            flexShrink: 0,
          }}
        >
          <Icon name="plus" size={10}/>
          <span>Папка</span>
        </button>
      </div>

      {folderCtx && createPortal(
        <div
          className="folder-ctx-menu chat-context-menu"
          style={{ position: "fixed", top: folderCtx.y, left: folderCtx.x, zIndex: 2147483000, minWidth: 200 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="chat-context-item"
            onClick={() => {
              setEditFolder(folderCtx.folder);
              setFolderCtx(null);
            }}
          >
            <span className="chat-context-item-icon"><Icon name="edit" size={14}/></span>
            Изменить
          </button>
          <button
            type="button"
            className="chat-context-item is-danger"
            onClick={() => {
              const f = folderCtx.folder;
              setFolderCtx(null);
              if (confirm(`Удалить папку «${f.name}»?`)) removeMut.mutate(f.id);
            }}
          >
            <span className="chat-context-item-icon"><Icon name="trash" size={14}/></span>
            Удалить
          </button>
        </div>,
        document.body
      )}
      {editFolder && (
        <NewFolderModal
          topics={topics}
          editFolder={editFolder}
          onClose={() => setEditFolder(null)}
        />
      )}
      {manageOpen && (
        <NewFolderModal
          topics={topics}
          onClose={() => {
            setManageOpen(false);
            qc.invalidateQueries({ queryKey: ["chat/folders"] });
          }}
        />
      )}
    </>
  );
}

function FolderTab({ active, label, onClick, onContextMenu, folderId, folderName, svgPath, emoji }: {
  active: boolean; label: string; onClick: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  folderId?: number; folderName?: string;
  svgPath?: string; emoji?: string;
}) {
  return (
    <button
      type="button"
      data-folder-id={folderId ?? ""}
      data-folder-name={folderName ?? ""}
      onClick={onClick}
      onContextMenu={onContextMenu}
      style={{
        background: "transparent",
        color: active ? "var(--tg-accent, #4fae4e)" : "var(--ink-3)",
        border: "none",
        cursor: "pointer",
        padding: "8px 12px 6px",
        position: "relative",
        fontSize: 13, fontWeight: active ? 600 : 500,
        whiteSpace: "nowrap",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        flexShrink: 0,
        transition: "color 0.12s ease",
      }}
    >
      {svgPath && (
        <svg viewBox="0 0 24 24" width="14" height="14" style={{ flexShrink: 0 }}>
          <path fill="currentColor" d={svgPath}/>
        </svg>
      )}
      {emoji && <span style={{ fontSize: 14, lineHeight: 1 }}>{emoji}</span>}
      <span>{label}</span>
      {active && (
        <span style={{
          position: "absolute",
          left: 8, right: 8, bottom: 0,
          height: 2,
          background: "var(--tg-accent, #4fae4e)",
          borderRadius: 2,
        }}/>
      )}
    </button>
  );
}

void ManageFoldersModal;
function ManageFoldersModal({ folders, onClose }: {
  folders: ChatFolder[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [newName, setNewName] = useState("");
  const [newIcon, setNewIcon] = useState("");

  const createMut = useMutation({
    mutationFn: () => createChatFolder({ name: newName.trim(), icon: newIcon.trim() || null }),
    onSuccess: () => {
      setNewName(""); setNewIcon("");
      qc.invalidateQueries({ queryKey: ["chat/folders"] });
    },
    onError: (e: Error) => alert(`Не удалось создать: ${e.message}`),
  });
  const editMut = useMutation({
    mutationFn: (vars: { id: number; name: string; icon: string | null }) =>
      editChatFolder(vars.id, { name: vars.name, icon: vars.icon }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat/folders"] }),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteChatFolder(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat/folders"] }),
  });

  return (
    <div
      className="mng-folders-backdrop"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1500,
        background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        className="mng-folders-sheet"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(440px, 92vw)", maxHeight: "90vh",
          background: "var(--paper)", borderRadius: 12,
          boxShadow: "0 12px 48px rgba(0,0,0,0.28)",
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}
      >
        <div className="mng-folders-head" style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 16px", borderBottom: "1px solid var(--line)",
        }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Папки чатов</h3>
          <button
            type="button" onClick={onClose} title="Закрыть"
            className="mng-folders-close"
            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 4, color: "var(--mute)" }}
          >
            <Icon name="x" size={16}/>
          </button>
        </div>

        <div className="mng-folders-body" style={{ padding: 16, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Existing folders */}
          {folders.length === 0 && (
            <div style={{ color: "var(--mute)", padding: 8 }}>Пока нет ни одной папки</div>
          )}
          {folders.map(f => (
            <div key={f.id} className="mng-folders-row" style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "6px 0", borderBottom: "1px solid var(--line)",
            }}>
              <input
                className="input"
                defaultValue={f.icon || ""}
                placeholder="📁"
                style={{ width: 50, textAlign: "center" }}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v !== (f.icon || "")) editMut.mutate({ id: f.id, name: f.name, icon: v || null });
                }}
              />
              <input
                className="input"
                defaultValue={f.name}
                style={{ flex: 1 }}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v && v !== f.name) editMut.mutate({ id: f.id, name: v, icon: f.icon });
                }}
              />
              <button
                type="button"
                onClick={() => {
                  if (confirm(`Удалить папку «${f.name}»? Топики останутся, только потеряют группировку.`)) {
                    deleteMut.mutate(f.id);
                  }
                }}
                title="Удалить"
                style={{
                  background: "transparent", border: "none", cursor: "pointer",
                  padding: 6, color: "var(--brick)",
                }}
              >
                <Icon name="trash" size={14}/>
              </button>
            </div>
          ))}

          {/* Create new */}
          <div className="mng-folders-new" style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
            <span style={{ fontSize: 11, color: "var(--mute)", textTransform: "uppercase" }}>
              Новая папка
            </span>
            <div className="mng-folders-new-row" style={{ display: "flex", gap: 8 }}>
              <input
                className="input"
                value={newIcon}
                onChange={(e) => setNewIcon(e.target.value)}
                placeholder="📁"
                style={{ width: 50, textAlign: "center" }}
              />
              <input
                className="input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Название"
                style={{ flex: 1 }}
                onKeyDown={(e) => { if (e.key === "Enter" && newName.trim()) createMut.mutate(); }}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={!newName.trim() || createMut.isPending}
                onClick={() => createMut.mutate()}
              >
                Создать
              </button>
            </div>
          </div>
        </div>

        <div className="mng-folders-foot" style={{
          display: "flex", justifyContent: "flex-end", gap: 8,
          padding: "12px 16px", borderTop: "1px solid var(--line)",
        }}>
          <button type="button" className="btn mng-folders-done" onClick={onClose}>Готово</button>
        </div>
      </div>
    </div>
  );
}
